import React, { useState, useEffect } from 'react';
import { withAllContexts } from '../../AllContextsWrapper';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { Icon } from '../Icons';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const AnalyticsPage = ({ app, socketIO }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Date range state - default to full current year
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    to: new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0]
  });

  const activeCalendarId = app.state.activeCalendar?.id;

  useEffect(() => {
    if (activeCalendarId) {
      fetchData();
    }
  }, [activeCalendarId, dateRange]);

  const fetchData = () => {
    setLoading(true);
    setError(null);
    socketIO.socket.emit('analytics:getData', { 
      calendarId: activeCalendarId,
      from: dateRange.from,
      to: dateRange.to
    }, (res) => {
      setLoading(false);
      if (res && res.success) {
        setData(res.data);
      } else {
        setError(res?.error || 'Nepodarilo sa načítať dáta');
      }
    });
  };

  const handleDateChange = (e) => {
    setDateRange({ ...dateRange, [e.target.name]: e.target.value });
  };

  const setPresetDate = (preset) => {
    const today = new Date();
    let from, to;
    if (preset === 'this_year') {
      from = new Date(today.getFullYear(), 0, 1);
      to = new Date(today.getFullYear(), 11, 31);
    } else if (preset === 'last_year') {
      from = new Date(today.getFullYear() - 1, 0, 1);
      to = new Date(today.getFullYear() - 1, 11, 31);
    } else if (preset === 'this_month') {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (preset === 'last_month') {
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth(), 0);
    }
    setDateRange({
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0]
    });
  };

  if (!activeCalendarId) {
    return <div className="p-8 text-center text-gray-500">Zvoľte kalendár pre zobrazenie analytiky.</div>;
  }

  const renderHeatmap = () => {
    if (!data || !data.heatmapData) return null;
    const maxHeat = Math.max(...data.heatmapData.flatMap(d => d.data.map(h => h.value)), 1);

    return (
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-2 overflow-hidden">
        <h3 className="text-lg font-bold text-gray-900 mb-6">Kedy ľudia rezervujú (Deň a hodina vytvorenia)</h3>
        <div className="overflow-x-auto pb-4">
          <table className="w-full text-xs text-center border-collapse min-w-[600px]">
            <thead>
              <tr>
                <th className="p-2 border-b border-gray-200"></th>
                {[...Array(24)].map((_, i) => <th key={i} className="p-2 border-b border-gray-200 font-medium text-gray-500">{i}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.heatmapData.map((day, dIdx) => (
                <tr key={dIdx}>
                  <td className="p-2 border-r border-gray-200 font-medium text-gray-500 text-left whitespace-nowrap">{day.name}</td>
                  {day.data.map((hour, hIdx) => {
                    const intensity = hour.value / maxHeat;
                    return (
                      <td key={hIdx} className="p-[2px]">
                        <div 
                          title={`${day.name} ${hour.hour}:00 - ${hour.value} rezervácií`}
                          className={`w-full h-8 rounded-sm transition-all duration-300 ${intensity === 0 ? 'bg-gray-50' : 'cursor-pointer hover:ring-2 hover:ring-blue-400'}`}
                          style={intensity > 0 ? { backgroundColor: `rgba(59, 130, 246, ${intensity * 0.8 + 0.2})` } : {}}
                        >
                          <span className="opacity-0 hover:opacity-100 flex items-center justify-center h-full text-white font-bold drop-shadow-md">
                            {hour.value > 0 ? hour.value : ''}
                          </span>
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const isCamping = data?.calendarType === 'accommodation' || data?.calendarType === 'camping';

  return (
    <div className="flex-1 h-full overflow-y-auto bg-gray-50 p-6 pt-20">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header & Controls */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="mb-4 xl:mb-0">
            <h1 className="text-2xl font-bold text-gray-900">Analytika a Prehľady</h1>
            <p className="text-sm text-gray-500 mt-1">
              Kalendár: {app.state.activeCalendar?.name} ({isCamping ? 'Táborisko' : 'Požičovňa'})
              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] rounded uppercase font-bold">
                Nezabudni nastaviť ceny v nastaveniach kalendára
              </span>
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setPresetDate('this_month')} className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors">Tento mesiac</button>
              <button onClick={() => setPresetDate('last_month')} className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors">Minulý mesiac</button>
              <button onClick={() => setPresetDate('this_year')} className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors">Tento rok</button>
              <button onClick={() => setPresetDate('last_year')} className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors">Minulý rok</button>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Od</label>
                <input 
                  type="date" 
                  name="from"
                  value={dateRange.from} 
                  onChange={handleDateChange}
                  className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Do</label>
                <input 
                  type="date" 
                  name="to"
                  value={dateRange.to} 
                  onChange={handleDateChange}
                  className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3">
            <Icon icon="exclamation-circle" />
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Skutočné tržby</p>
                <p className="text-xl font-bold text-green-600">{data.metrics.totalRevenueActual} €</p>
              </div>
              
              <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Očakávané tržby</p>
                <p className="text-xl font-bold text-blue-600">{data.metrics.totalRevenueExpected} €</p>
              </div>

              <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Dlžné sumy</p>
                <p className="text-xl font-bold text-red-500">{data.metrics.outstandingBalance} €</p>
              </div>

              <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Ušlá tržba (Storno)</p>
                <p className="text-xl font-bold text-gray-400">{data.metrics.lostRevenue} €</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Aktívne Rezervácie</p>
                <p className="text-xl font-bold text-purple-600">{data.metrics.totalReservations}</p>
              </div>

              <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Zrušené (Storno)</p>
                <p className="text-xl font-bold text-red-400">{data.metrics.cancelledCount}</p>
              </div>

              <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Vopred o</p>
                <p className="text-xl font-bold text-orange-600">{data.metrics.averageAdvanceBooking} dní</p>
              </div>
            </div>

            {/* Camping Specific Metrics */}
            {isCamping && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100 text-center">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Dospelí</p>
                  <p className="text-2xl font-black text-emerald-900">{data.metrics.camping.adults}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100 text-center">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Deti</p>
                  <p className="text-2xl font-black text-emerald-900">{data.metrics.camping.children}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100 text-center">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Stany</p>
                  <p className="text-2xl font-black text-emerald-900">{data.metrics.camping.tents}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100 text-center">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Autá</p>
                  <p className="text-2xl font-black text-emerald-900">{data.metrics.camping.cars}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100 text-center">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Karavany</p>
                  <p className="text-2xl font-black text-emerald-900">{data.metrics.camping.caravans}</p>
                </div>
              </div>
            )}

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Revenue Over Time */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                   <Icon icon="chart-bar" className="text-blue-500" />
                   Vývoj tržieb (Mesačne)
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.chartDataRevenue}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#9ca3af'}} />
                      <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#9ca3af'}} tickFormatter={(value) => `${value}€`} />
                      <Tooltip 
                        formatter={(value) => `${value} €`}
                        cursor={{fill: '#f8fafc'}}
                        contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
                      />
                      <Legend />
                      <Bar name="Skutočné" dataKey="Skutočné" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar name="Očakávané" dataKey="Očakávané" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Categorized Revenue (Actual) */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                   <Icon icon="tags" className="text-emerald-500" />
                   Rozdelenie tržieb (Skutočné)
                </h3>
                <div className="h-80">
                  {data.chartDataCategories.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.chartDataCategories}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={110}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {data.chartDataCategories.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} strokeWidth={0} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value} €`} contentStyle={{borderRadius: '12px', border: 'none'}} />
                        <Legend layout="vertical" verticalAlign="middle" align="right" />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400">
                      Nedostatok dát pre rozdelenie položiek
                    </div>
                  )}
                </div>
              </div>

              {/* Stay Types (Camping Only) */}
              {isCamping && (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Icon icon="campground" className="text-emerald-500" />
                    Typy pobytov
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.chartDataStayTypes} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                          {data.chartDataStayTypes.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Payment Types & Languages Row */}
              <div className={`bg-white p-6 rounded-2xl shadow-sm border border-gray-100 ${isCamping ? 'lg:col-span-1' : 'lg:col-span-1'}`}>
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                   <Icon icon="credit-card" className="text-indigo-500" />
                   Typy platieb
                </h3>
                <div className="h-64">
                   <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.chartDataPaymentTypes} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                          {data.chartDataPaymentTypes.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value} €`} />
                      </PieChart>
                   </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-1">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                   <Icon icon="globe" className="text-cyan-500" />
                   Jazyky
                </h3>
                <div className="h-64">
                   <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.chartDataLanguages} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                          {data.chartDataLanguages.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                   </ResponsiveContainer>
                </div>
              </div>

              {/* Logistics (Places) - only for rafting */}
              {!isCamping && (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Icon icon="map-marker-alt" className="text-red-500" />
                    Najvyťaženejšie miesta (Logistika)
                  </h3>
                  <div className="h-80">
                    {data.chartDataPlaces.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.chartDataPlaces} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f0f0f0" />
                          <XAxis type="number" hide />
                          <YAxis dataKey="name" type="category" width={100} axisLine={false} tickLine={false} tick={{fontSize: 11}} />
                          <Tooltip contentStyle={{borderRadius: '12px', border: 'none'}} />
                          <Legend />
                          <Bar name="Počet" dataKey="Count" fill="#F43F5E" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-400">
                          Žiadne dáta o miestach nástupu/výstupu
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Top Clients */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                   <Icon icon="crown" className="text-yellow-500" />
                   Top 5 Klientov (podľa obratu)
                </h3>
                <div className="overflow-hidden">
                   <table className="w-full text-left">
                      <thead>
                         <tr className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            <th className="pb-4">Klient</th>
                            <th className="pb-4 text-right">Obrat</th>
                         </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                         {data.topClientsData.map((client, idx) => (
                            <tr key={idx} className="group hover:bg-gray-50 transition-colors">
                               <td className="py-4 font-medium text-gray-700">{client.name}</td>
                               <td className="py-4 text-right font-bold text-green-600">{client.revenue.toFixed(2)} €</td>
                            </tr>
                         ))}
                      </tbody>
                   </table>
                </div>
              </div>

              {/* Occupancy Trend */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                   <Icon icon="calendar-day" className="text-purple-500" />
                   Trend vyťaženosti
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.chartDataOccupancy}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#9ca3af'}} minTickGap={40} />
                      <YAxis axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#9ca3af'}} />
                      <Tooltip contentStyle={{borderRadius: '12px', border: 'none'}} />
                      <Line type="monotone" dataKey="Rezervácie" stroke="#8B5CF6" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Duration Stats */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                   <Icon icon="hourglass-half" className="text-orange-500" />
                   Trvanie rezervácií
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.chartDataDurationStats}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#9ca3af'}} />
                      <YAxis axisLine={false} tickLine={false} tick={{fontSize: 11, fill: '#9ca3af'}} />
                      <Tooltip formatter={(value) => `${value} rezervácií`} contentStyle={{borderRadius: '12px', border: 'none'}} />
                      <Bar name="Rezervácie" dataKey="Rezervácie" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Heatmap */}
              {renderHeatmap()}

            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default withAllContexts(AnalyticsPage);
